import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import effectOsperb from '../../assets/img/mobileLanding/effect-osperb.png';
import { E_COMMERCE_STYLES } from 'components/ECommerce/config';


interface FeedbackProps {
    type?: string;
}

// Helper function to extract YouTube video ID and get thumbnail
const getYouTubeThumbnail = (url: string | null): string | null => {
    if (!url) return null;

    const videoId = getYouTubeVideoId(url);
    if (videoId) {
        return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    }

    return null;
};

// Helper function to extract YouTube video ID
const getYouTubeVideoId = (url: string | null): string | null => {
    if (!url) return null;

    let videoId = null;

    // Handle youtu.be format
    if (url.includes('youtu.be/')) {
        videoId = url.split('youtu.be/')[1]?.split('?')[0];
    }
    // Handle youtube.com/watch format
    else if (url.includes('youtube.com/watch')) {
        const urlParams = new URLSearchParams(url.split('?')[1]);
        videoId = urlParams.get('v');
    }
    // Handle youtube.com/embed format
    else if (url.includes('youtube.com/embed/')) {
        videoId = url.split('youtube.com/embed/')[1]?.split('?')[0];
    }
    // Handle youtube.com/shorts format
    else if (url.includes('youtube.com/shorts/')) {
        videoId = url.split('youtube.com/shorts/')[1]?.split('?')[0];
    }

    return videoId;
};

// Get YouTube embed URL with autoplay and hidden controls
const getYouTubeEmbedUrl = (url: string | null): string | null => {
    if (!url) return null;

    const videoId = getYouTubeVideoId(url);
    if (videoId) {
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        return `https://www.youtube.com/embed/${videoId}?autoplay=1&controls=0&showinfo=0&modestbranding=1&rel=0&fs=0&iv_load_policy=3&disablekb=1&playsinline=1&mute=0&loop=1&playlist=${videoId}&origin=${origin}`;
    }

    return null;
};

function Feedback({ type }: FeedbackProps) {
    // Track which testimonial video is currently playing (by testimonial ID)
    const [playingVideoId, setPlayingVideoId] = useState<number | null>(null);

    const handlePlayVideo = (testimonialId: number) => {
        setPlayingVideoId(testimonialId);
    };

    const handleStopVideo = () => {
        setPlayingVideoId(null);
    };

    const testimonials = [
        {
            id: 1,
            name: 'Mr. Ahmed Al Jabiri',
            position: 'State council',
            company: 'Oman',
            project: '',
            rating: 5,
            quote: '“Technology is the 4th capital of any successful business, and what they built for us is more than just a website. it’s the foundation for our future growth ”',
            videoUrl: 'https://youtube.com/shorts/M5SNQrFE_Vo',
        },
        {
            id: 2,
            name: 'Mr. Vivek P Nair',
            position: 'General Manager',
            company: 'Focus Motors',
            project: '',
            rating: 5,
            quote: 'They built exactly what we needed. a clean, workflow-matched tech ecosystem making it simple to run the entire business even as a one-man operation',
            videoUrl: 'https://youtube.com/shorts/S7ms8cHQVQ8?si=MavDCZJSJQAeOx0_',
        },
    ];


    return (
        <Box
            sx={{
                backgroundColor: '#000',
                padding: '60px 12px 20px',
                pt: 10,
                position: 'relative',


            }}
        >
            <Box
                sx={{
                    maxWidth: E_COMMERCE_STYLES.container.maxWidthWeb,
                    margin: E_COMMERCE_STYLES.container.mx,
                }}
            >
                {/* Section Title */}
                <Box sx={{ textAlign: 'center', mb: 5, }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60px" }}>
                        <img src={effectOsperb} alt="" />
                    </Box>
                    <Typography
                        variant="subtitle1"
                        className='font-satoshi-regular'
                        sx={{
                            fontSize: '14px',
                            fontWeight: 400,
                            lineHeight: '113%',
                            textAlign: 'center',
                            color: '#fff',
                        }}
                    >
                        Real Results - In Our Clients' Words
                    </Typography>
                </Box>

                {/* Testimonials List */}
                <Box sx={{ display: 'flex', flexDirection: type === 'web' ? 'row' : 'column', gap: 3, mb: type === 'web' ? 3 : 2 }}>
                    {testimonials.map((testimonial, index) => {
                        const isPlaying = playingVideoId === testimonial.id;

                        return (
                            <Box
                                key={`testimonial-${index}`}
                                sx={{
                                    background: 'linear-gradient(149.06deg, rgba(156, 255, 187, 0.18) 3.05%, rgba(190, 205, 255, 0.18) 97.6%)',
                                    border: '1px solid #FFFFFF33',
                                    cornerShape: 'squircle',
                                    borderRadius: '45px',
                                    padding: type === 'web' ? '40px' : '40px 24px',
                                    maxWidth: type === 'web' ? '100%' : '600px',
                                    mx: 'auto',
                                    width: '100%',
                                }}
                            >
                                {type === 'web' ? (
                                    // Web Layout: Video on left, Stars + Quote on right
                                    <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                        {/* Left Side: Video and Client Info */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            {/* Video Player */}
                                            <Box
                                                sx={{
                                                    position: 'relative',
                                                    width: '186px',
                                                    height: '186px',
                                                    flexShrink: 0,
                                                    borderRadius: '36px',
                                                    cornerShape: 'squircle',
                                                    overflow: 'hidden',
                                                    background: '#00000033',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        border: '2.86px solid #FFFFFF66',
                                                        borderRadius: '36px',
                                                        cornerShape: 'squircle',
                                                        zIndex: 10,
                                                        pointerEvents: 'none',
                                                    }}
                                                />
                                                {isPlaying ? (
                                                    // Show YouTube iframe when playing with pause overlay on hover
                                                    <Box
                                                        onClick={handleStopVideo}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            cursor: 'pointer',
                                                            '&:hover .pause-overlay': {
                                                                opacity: 1,
                                                            },
                                                        }}
                                                    >
                                                        <iframe
                                                            src={getYouTubeEmbedUrl(testimonial.videoUrl) || ''}
                                                            title="YouTube Video Player"
                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                            style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                border: 'none',
                                                                pointerEvents: 'none',
                                                            }}
                                                        />
                                                        {/* Pause Overlay - Shows on hover */}
                                                        <Box
                                                            className="pause-overlay"
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                opacity: 0,
                                                                transition: 'opacity 0.3s ease',
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: '50px',
                                                                    height: '50px',
                                                                    borderRadius: '50%',
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <PauseIcon sx={{ color: '#333', fontSize: '1.5rem' }} />
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    // Show Thumbnail with Play Button when not playing
                                                    <Box
                                                        onClick={() => handlePlayVideo(testimonial.id)}
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            backgroundColor: '#333',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            position: 'relative',
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        {getYouTubeThumbnail(testimonial.videoUrl) ? (
                                                            <Box
                                                                component="img"
                                                                src={getYouTubeThumbnail(testimonial.videoUrl) || ''}
                                                                alt={testimonial.name}
                                                                sx={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover',
                                                                }}
                                                            />
                                                        ) : (
                                                            <Typography sx={{ color: '#666', fontSize: '0.75rem' }}>
                                                                Video
                                                            </Typography>
                                                        )}

                                                        {/* Play Button Overlay */}
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: '50%',
                                                                left: '50%',
                                                                transform: 'translate(-50%, -50%)',
                                                                width: '50px',
                                                                height: '50px',

                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer',
                                                                transition: 'transform 0.3s ease',
                                                                '&:hover': {
                                                                    transform: 'translate(-50%, -50%) scale(1.1)',
                                                                },
                                                            }}
                                                        >
                                                            <PlayArrowIcon sx={{ width: '35px', height: '50px', color: '#FF2121' }} />
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Box>

                                        {/* Right Side: Stars and Quote */}
                                        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                            {/* Star Rating */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    gap: 0.5,
                                                }}
                                            >
                                                {[...Array(testimonial.rating)].map((_, starIndex) => (
                                                    <StarRoundedIcon key={starIndex} fontSize='small' sx={{ color: '#FFD700' }} />
                                                ))}
                                            </Box>

                                            {/* Quote */}
                                            <Typography
                                                variant="body1"
                                                className='font-satoshi-medium-italic'
                                                sx={{
                                                    color: '#FFFFFFBA',

                                                    fontSize: '20px',
                                                    fontWeight: 500,
                                                    fontStyle: 'italic',
                                                    lineHeight: '110%',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                {testimonial.quote}
                                            </Typography>

                                            {/* Client Info */}
                                            <Box sx={{ textAlign: 'left' }}>
                                                <Typography
                                                    variant="h6"
                                                    className="font-satoshi-bold"
                                                    sx={{
                                                        color: '#FFFFFF',
                                                        fontSize: '14px',
                                                        fontWeight: 700,
                                                        lineHeight: '110%',
                                                        mb: 1,
                                                    }}
                                                >
                                                    {testimonial.name}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    className="font-satoshi-italic"
                                                    sx={{
                                                        color: '#FFFFFF99',
                                                        fontSize: '13px',
                                                        fontWeight: 400,
                                                        fontStyle: 'italic',
                                                        lineHeight: '100%',
                                                    }}
                                                >
                                                    {testimonial.position} —<br />
                                                    {testimonial.company}<br />
                                                    {testimonial.project}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                ) : (
                                    // Mobile Layout: Original vertical layout
                                    <>
                                        {/* Star Rating */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                gap: 0.5,
                                                mb: 3,
                                            }}
                                        >
                                            {[...Array(testimonial.rating)].map((_, starIndex) => (
                                                <StarRoundedIcon key={starIndex} fontSize='small' sx={{ color: '#FFD700' }} />
                                            ))}
                                        </Box>

                                        {/* Quote */}
                                        <Typography
                                            variant="body1"
                                            className='font-satoshi-medium-italic'
                                            sx={{
                                                color: '#FFFFFFBA',
                                                fontSize: '20px',
                                                fontWeight: 500,
                                                fontStyle: 'italic',
                                                lineHeight: '110%',
                                                textAlign: 'center',
                                                mb: 4,
                                                px: 2,
                                            }}
                                        >
                                            {testimonial.quote}
                                        </Typography>

                                        {/* Video and Client Info Container */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'end',
                                                gap: 2,
                                            }}
                                        >
                                            {/* Video Player */}
                                            {/* Video Player */}
                                            <Box
                                                sx={{
                                                    position: 'relative',
                                                    width: '180px',
                                                    height: '180px',
                                                    flexShrink: 0,
                                                    borderRadius: '36px',
                                                    cornerShape: 'squircle',
                                                    overflow: 'hidden',
                                                    background: '#00000033',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        border: '2.86px solid #FFFFFF66',
                                                        borderRadius: '36px',
                                                        cornerShape: 'squircle',
                                                        zIndex: 10,
                                                        pointerEvents: 'none',
                                                    }}
                                                />
                                                {isPlaying ? (
                                                    // Show YouTube iframe when playing with pause overlay on hover
                                                    <Box
                                                        onClick={handleStopVideo}
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            width: '100%',
                                                            height: '100%',
                                                            cursor: 'pointer',
                                                            '&:hover .pause-overlay': {
                                                                opacity: 1,
                                                            },
                                                        }}
                                                    >
                                                        <iframe
                                                            src={getYouTubeEmbedUrl(testimonial.videoUrl) || ''}
                                                            title="YouTube Video Player"
                                                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                            style={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                border: 'none',
                                                                pointerEvents: 'none',
                                                            }}
                                                        />
                                                        {/* Pause Overlay - Shows on hover */}
                                                        <Box
                                                            className="pause-overlay"
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                width: '100%',
                                                                height: '100%',
                                                                backgroundColor: 'rgba(0, 0, 0, 0.4)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                opacity: 0,
                                                                transition: 'opacity 0.3s ease',
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: '50px',
                                                                    height: '50px',
                                                                    borderRadius: '50%',
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                }}
                                                            >
                                                                <PauseIcon sx={{ color: '#333', fontSize: '1.5rem' }} />
                                                            </Box>
                                                        </Box>
                                                    </Box>
                                                ) : (
                                                    // Show Thumbnail with Play Button when not playing
                                                    <Box
                                                        onClick={() => handlePlayVideo(testimonial.id)}
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            backgroundColor: '#333',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            position: 'relative',
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        {getYouTubeThumbnail(testimonial.videoUrl) ? (
                                                            <Box
                                                                component="img"
                                                                src={getYouTubeThumbnail(testimonial.videoUrl) || ''}
                                                                alt={testimonial.name}
                                                                sx={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover',
                                                                }}
                                                            />
                                                        ) : (
                                                            <Typography sx={{ color: '#666', fontSize: '0.75rem' }}>
                                                                Video
                                                            </Typography>
                                                        )}

                                                        {/* Play Button Overlay */}
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: '50%',
                                                                left: '50%',
                                                                transform: 'translate(-50%, -50%)',
                                                                width: '50px',
                                                                height: '50px',

                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer',
                                                                transition: 'transform 0.3s ease',
                                                                '&:hover': {
                                                                    transform: 'translate(-50%, -50%) scale(1.1)',
                                                                },
                                                            }}
                                                        >
                                                            <PlayArrowIcon sx={{ width: '25.23px', height: '29.82px', color: '#FF2121' }} />
                                                        </Box>
                                                    </Box>
                                                )}
                                            </Box>

                                            {/* Client Info */}
                                            <Box mb={2} sx={{ textAlign: 'left', flex: 1 }}>
                                                <Typography
                                                    variant="h6"
                                                    className="font-satoshi-bold"
                                                    sx={{
                                                        color: '#FFFFFF',
                                                        fontSize: '14px',
                                                        fontWeight: 700,
                                                        lineHeight: '110%',
                                                        mb: 1,
                                                    }}
                                                >
                                                    {testimonial.name}
                                                </Typography>
                                                <Typography
                                                    variant="body2"
                                                    className="font-satoshi-italic"
                                                    sx={{
                                                        color: '#FFFFFF99',
                                                        fontSize: '13px',
                                                        fontWeight: 400,
                                                        fontStyle: 'italic',
                                                        lineHeight: '100%',
                                                    }}
                                                >
                                                    {testimonial.position} —<br />
                                                    {testimonial.company}<br />
                                                    {testimonial.project}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            </Box >
        </Box >
    );
}

export default Feedback;

